import { memo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { DivWithTitle } from 'src/core/ui/DivWithTitle';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FormatOption } from 'src/modals/EditTableColumns';
import { CustomOnSaleEventColumn } from 'src/navigations/Routes/OnSaleDiscovery/OnSaleDiscoveryView.types';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { getColumnPersonalization } from 'src/utils/columns/columnPersonalizationUtils';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { CustomSalesColumn } from 'src/utils/columns/sales/salesCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { SectionType } from 'src/utils/types/sectionType';
import { Feature, ReportTag, Tag } from 'src/WebApiController';

import * as styles from './CustomNumericCell.css';

type CustomNumericCellProps = {
  columnId: string;
  userDefinedPrecision: FormatOption | undefined;
  value: number | null;
  isLoading: boolean;
  hide?: boolean;
  shimmeringDivHeight: string;
  sectionType: SectionType;
  matchingCustomColumn?:
    | CustomListingColumn
    | CustomSalesColumn
    | CustomOnSaleEventColumn;
  matchingTag?: Tag | ReportTag;
  currencyCode?: string;
  isPercent?: boolean;
  colorScheme?: 'profit' | 'loss';
};

const CustomNumericCellRaw = ({
  columnId,
  userDefinedPrecision,
  value,
  isLoading,
  hide,
  shimmeringDivHeight,
  sectionType,
  matchingCustomColumn,
  matchingTag,
  currencyCode,
  isPercent,
  colorScheme,
}: CustomNumericCellProps) => {
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();
  const uiCurrency = getUiCurrency(
    currencyCode ?? loginContext?.user?.activeAccount.currencyCode
  );
  const hasCustomPurchaseColumns = useUserHasFeature(
    Feature.CustomPurchaseColumns
  );

  const { isCurrency: defaultIsCurrency, isPercent: defaultIsPercent } =
    getColumnPersonalization({
      id: columnId,
      sectionType,
      matchingCustomColumn,
      matchingTag,
    });
  const isCurrency =
    hasCustomPurchaseColumns && matchingCustomColumn?.formula
      ? !!defaultIsCurrency
      : currencyCode != null || !!defaultIsCurrency;
  isPercent = isPercent ?? defaultIsPercent;

  if (isLoading) {
    return <TableShimmeringDiv height={shimmeringDivHeight} />;
  }

  if (hide) {
    return null;
  }

  return (
    <TableCellDiv
      align={colorScheme != null ? 'left' : 'right'}
      showTooltip={false}
    >
      <DivWithTitle
        className={
          colorScheme === 'profit'
            ? styles.profitColor
            : colorScheme === 'loss'
              ? styles.lossColor
              : undefined
        }
      >
        {value == null ? (
          ''
        ) : isNaN(value) || !isFinite(value) ? (
          <Content id={ContentId.NA} />
        ) : (
          <>
            {applyNumberFormatting({
              precision: userDefinedPrecision,
              inputNumber: value,
              currencyCode: isCurrency ? uiCurrency.code : undefined,
              currencyDecimalDigits: isCurrency ? uiCurrency.dec : undefined,
              isPercent: isPercent && !isCurrency,
            })}
          </>
        )}
      </DivWithTitle>
    </TableCellDiv>
  );
};

export const CustomNumericCell =
  memo<CustomNumericCellProps>(CustomNumericCellRaw);
